/**
 * Which queries each realtime event refreshes. `useSseInvalidations` applies
 * these; a model without a rule refreshes its own family, by id.
 *
 * Keys are model-first (`["Family", "scope", ...args]`, see `api/queryKeys.ts`).
 * For one query, a target naming its scope wins over one that names only its
 * family.
 */

import { InvalidatableModels, type InvalidatableModel } from "../api/queryKeys.ts";

export interface Target {
  /** The key's first segment. */
  family: string;
  /** Its second segment; every one when omitted. */
  scopes?: readonly string[];
  /**
   * "id": the queries whose key holds the event's id, or all of them when the
   * event has none (many rows changed). "any": all of them, whatever the id.
   */
  match: "id" | "any";
  /** At most one refetch per query per window (leading and trailing edge). */
  throttleMs?: number;
}

// Dashboards are expensive aggregates that many events touch.
const DASHBOARD_THROTTLE_MS = 10_000;
// The signal engine and agent runs can change a tenant's prospects and signals
// several times a second; their lists follow at most this often.
const LIST_THROTTLE_MS = 3_000;

const own = (family: string): Target => ({ family, match: "id" });
const all = (family: string, scopes?: readonly string[]): Target => ({ family, scopes, match: "any" });
const busy = (family: string, scopes: readonly string[]): Target => ({
  family,
  scopes,
  match: "any",
  throttleMs: LIST_THROTTLE_MS,
});
const dashboard = (family: string): Target => ({
  family,
  scopes: ["dashboard"],
  match: "any",
  throttleMs: DASHBOARD_THROTTLE_MS,
});
const ORG_DASHBOARD = dashboard("Organization");

export const RULES: Partial<Record<InvalidatableModel, readonly Target[]>> = {
  // A run starting or finishing moves the "agent at work" marker
  // (`active_agent_runs`) on what it works on, a signal's research, an order's
  // runs and the steps they settle, and the end of its transcript.
  AgentRun: [
    busy("Signal", ["list", "count"]),
    all("Signal", ["research"]),
    busy("TenantProspect", ["list", "count"]),
    all("TenantProspect", ["detail"]),
    all("ProspectList", ["list", "detail", "overview"]),
    all("ProspectOrder", ["list", "detail", "agentRuns", "workflows"]),
    all("WorkflowScheduleRun", ["list"]),
    { family: "AgentRun", scopes: ["transcript"], match: "id" },
    dashboard("Signal"),
    dashboard("TenantProspect"),
    ORG_DASHBOARD,
  ],
  // A schedule workflow fired (id = the run's id): its runs and its next slot.
  WorkflowScheduleRun: [all("WorkflowScheduleRun", ["list"]), all("Workflow", ["list", "detail"])],
  // A transcript line (id = the run's id); the org dashboard shows each live run's current step.
  AgentRunEvent: [{ family: "AgentRun", scopes: ["transcript"], match: "id" }, ORG_DASHBOARD],
  // A change to one prospect moves it between queues. Reviews (Pursue,
  // Disqualify, snooze) come as these too: a signal row's review state is its
  // prospect's.
  TenantProspect: [
    own("TenantProspect"),
    busy("TenantProspect", ["list", "count"]),
    busy("Signal", ["list", "count"]),
    dashboard("TenantProspect"),
    dashboard("Signal"),
    ORG_DASHBOARD,
  ],
  Signal: [own("Signal"), busy("Signal", ["list", "count"]), dashboard("Signal"), ORG_DASHBOARD],
  // The collections list carries counts and the last update.
  ProspectList: [own("ProspectList"), all("ProspectList", ["list", "overview"])],
  // Id = the list's id. Its companies show through the prospects list,
  // filtered by collection; a signal row's review state follows its entries.
  ProspectListEntry: [
    own("ProspectList"),
    all("ProspectList", ["list", "overview"]),
    busy("TenantProspect", ["list", "count"]),
    busy("Signal", ["list", "count"]),
  ],
  ProspectListBulkAddJob: [own("ProspectList")],
  // Id = the list's id. A collection's rows count its people; the drawer lists them.
  ProspectListContactEntry: [own("ProspectListContactEntry"), all("TenantProspect", ["list", "detail"])],
  // Sent without an id: its queries are keyed by list and org, not by job.
  ContactEnrichmentJob: [all("ContactEnrichmentJob")],
  ProspectOrder: [own("ProspectOrder"), all("ProspectOrder", ["list"]), ORG_DASHBOARD],
  // Id = the order's id; the list counts comments.
  ProspectOrderComment: [own("ProspectOrder"), all("ProspectOrder", ["list"])],
  // Platform pages: one small family each.
  CsvImport: [all("CsvImport")],
  TrainingDataExport: [all("TrainingDataExport")],
  // Written on every heartbeat.
  InferenceInstance: [{ family: "InferenceInstance", match: "any", throttleMs: 5_000 }],
  InferenceRental: [all("InferenceRental", ["list"])],
  BackgroundJob: [all("BackgroundJob")],
  // A five-minute sample of the work queues was taken.
  PlatformQueues: [all("Platform", ["queues", "signalWorkflows"])],
  // The stats materialized views were refreshed.
  PlatformStats: [all("MasterCompany", ["stats"]), all("Platform", ["health"])],
  // A periodic job ticked or a durable task changed state: several a minute.
  RecurringJobs: [
    {
      family: "Platform",
      scopes: ["recurring", "recurringTicks"],
      match: "any",
      throttleMs: DASHBOARD_THROTTLE_MS,
    },
  ],
  // An email queued, tried, accepted or finished; a burst of invitations makes many.
  OutboundEmails: [{ family: "Platform", scopes: ["emails"], match: "any", throttleMs: DASHBOARD_THROTTLE_MS }],
  // A contact-data delivery, search or lookup; an agent buying makes bursts.
  DataProviderUsage: [
    { family: "Platform", scopes: ["dataProviderUsage"], match: "any", throttleMs: DASHBOARD_THROTTLE_MS },
  ],
};

export function rulesFor(model: InvalidatableModel): readonly Target[] {
  return RULES[model] ?? [own(model)];
}

/** The target among `targets` that governs the query `key`, if any. */
export function targetFor(targets: readonly Target[], key: readonly unknown[]): Target | undefined {
  let familyWide: Target | undefined;
  for (const t of targets) {
    if (key[0] !== t.family) continue;
    if (!t.scopes) familyWide ??= t;
    else if ((t.scopes as readonly unknown[]).includes(key[1])) return t;
  }
  return familyWide;
}

/** Whether an event with `id` refreshes the query `key` under `target`. */
export function fires(target: Target, key: readonly unknown[], id: string | null | undefined): boolean {
  return target.match === "any" || id == null || key.includes(id);
}

/** Every target of every model: what a missed stretch of events may have touched. */
export const LIVE_TARGETS: readonly Target[] = InvalidatableModels.flatMap((m) => rulesFor(m));
